import type { StrategyDirection } from './trading-strategy.types'

/** How a signal trade and the builder's trade compare. */
export enum TrackerTradeStatus {
  LinesUp = 1,
  /** Their returns are half a point or more apart. */
  PriceDiffers = 2,
  DatesDiffer = 3,
  NoSignal = 4,
  NoBuilderTrade = 5,
  /** Matched on dates, but the signal trade has no price. */
  NoPriceYet = 6,
}

/** The run's direction for a trade. */
export enum TrackerDirection {
  Long = 1,
  Short = 2,
}

// Response type for GET /api/portfolio-tracker/revisions/{revisionId}/strategies
export interface TrackerRevisionStrategies {
  revisionId: string
  /** The builder's side: LongAndShort or LongOnly. */
  side: StrategyDirection
  /** The server's UTC today. */
  asOf: string
  /** The earliest signal day across the strategies. Null when none has signalled. */
  firstSignal: string | null
  /** Ordered by name. */
  strategies: TrackerRevisionStrategy[]
}

export interface TrackerRevisionStrategy {
  /** The run's key for it. */
  key: string
  /** The side's public ID. */
  publicStrategyId: string
  /** The back office strategy with that public ID. Null when there is none. */
  tradingStrategyId: string | null
  name: string
  chartedSymbol: string | null
  bullSymbol: string | null
  bearSymbol: string | null
  riskProfile: number
  firstSignal: string | null
  /** How many signals it has. */
  signals: number
}

// Request body for POST /api/portfolio-tracker/check
export interface TrackerCheckRequest {
  revisionId: string
  /** The builder's side: LongAndShort or LongOnly. */
  side: StrategyDirection
  amount: number
  start: string
  /** A builder portfolio: up to six strategies, one per charted symbol, split evenly. */
  evenSplit: boolean
  strategies: { key: string; allocationPercent: number | null }[]
}

// Response type for POST /api/portfolio-tracker/check
export interface TrackerCheck {
  asOf: string
  start: string
  putIn: number
  /** The builder's own figures for the portfolio over its whole backtest. Null for an own split. */
  builderWholeBacktest: TrackerWholeBacktest | null
  /** The last exit in the revision's trades for these strategies. Null when they have none. */
  builderTradesTo: string | null
  signals: TrackerSideFigures
  builder: TrackerSideFigures
  /** In the order they were asked for. */
  strategies: TrackerCheckStrategy[]
  /** Every pair, newest exit first. */
  trades: TrackerTradePair[]
}

export interface TrackerWholeBacktest {
  from: string
  to: string
  perYearPercent: number
  realDrawdownPercent: number
}

export interface TrackerSideFigures {
  worth: number
  profit: number
  /** Positive: 4.3 is 4.3% below the high. */
  realDrawdownPercent: number
  longestWaitDays: number
  closedTrades: number
  won: number
  winRatePercent: number
  openTrades: number
  /** Signals side only. */
  unpricedTrades: number
  /** Signals side only. */
  alertPricedTrades: number
  /** Starts at the start with what was put in, a point per exit, ends today. */
  balance: { date: string; value: number }[]
  /** Each day's end, 0 or below, ends today. */
  fallFromHigh: { date: string; percent: number }[]
}

export interface TrackerStrategyTally {
  profit: number
  closed: number
  won: number
}

export interface TrackerCheckStrategy {
  key: string
  publicStrategyId: string
  tradingStrategyId: string | null
  name: string
  allocationPercent: number
  signals: TrackerStrategyTally
  builder: TrackerStrategyTally
  /** Its trades that don't line up. */
  differences: number
}

export interface TrackerTradePair {
  /** The strategy's key. */
  key: string
  status: TrackerTradeStatus
  /** The fund. */
  symbol: string
  /** The signal's return less the builder's, in points. Null unless both have one. */
  gapPoints: number | null
  signal: TrackerSignalTrade | null
  builder: TrackerBuilderTrade | null
}

export interface TrackerSignalTrade {
  buyDate: string
  /** Null while still open. */
  exitDate: string | null
  buyPrice: number | null
  exitPrice: number | null
  buyPricedAtAlert: boolean
  exitPricedAtAlert: boolean
  returnPercent: number | null
  amount: number | null
  profit: number | null
  buyIncomingSignalId: string
  exitIncomingSignalId: string | null
}

export interface TrackerBuilderTrade {
  buyDate: string
  /** Null while still open. */
  exitDate: string | null
  direction: TrackerDirection
  returnPercent: number | null
  amount: number | null
  profit: number | null
}
